import { useQuery } from '@tanstack/react-query';
import { Checkbox } from '@/components/ui/Checkbox';
import { api } from '@/shared/api/client';
import { useAuthStore } from '@/shared/store/authStore';
import { IconCheck } from '@/components/ui/Icon';
import { favoriteIcon } from '@/features/favorites/entityIcons';
import { useHomeNav } from '../useHomeNav';
import { registerWidget, type WidgetRenderProps, type WidgetConfigProps } from '../registry';

type Item = {
  doc_type: string; id: string; number: string | null; date: string | null; route: string;
  status_label: string; status_color: string | null;
  amount: number | null; party_name: string | null; currency: string | null;
  mine: boolean; needs_approval: boolean;
};
type MyWork = { assigned: Item[]; approvals: Item[] };

// Human label per doc type — shown as a small prefix so every row states its
// kind, and used as the fallback for the rare non-draft doc that has no number.
// Kept in lockstep with the zoom-crumb labels the Recents widget shows
// ("Invoice INV-…", "Bill D-…", "JE JE-…") so both widgets speak one language.
const TYPE_LABELS: Record<string, string> = {
  customer_invoice: 'Invoice', vendor_invoice: 'Bill', sales_order: 'Sales Order',
  sales_quote: 'Quote', purchase_order: 'Purchase Order', purchase_requisition: 'Requisition',
  journal_entry: 'JE', service_job: 'Service Job',
};

// Compact currency formatting (e.g. "$12.3K", "CA$1.2M"), one cached formatter
// per currency. Falls back to USD if a code is unrecognised by Intl.
const _fmtCache = new Map<string, Intl.NumberFormat>();
function moneyFmt(ccy: string): Intl.NumberFormat {
  let f = _fmtCache.get(ccy);
  if (!f) {
    try {
      f = new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy, notation: 'compact', maximumFractionDigits: 1 });
    } catch {
      f = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
    }
    _fmtCache.set(ccy, f);
  }
  return f;
}

const _dateMD = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const _dateMDY = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
// Format a 'YYYY-MM-DD' date string compactly; include the year only when it
// differs from the current one. Built from parts to avoid UTC parse shift.
function fmtDate(s: string): string {
  const [y, m, d] = s.split('-').map(Number);
  if (!y) return s;
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return (y === new Date().getFullYear() ? _dateMD : _dateMDY).format(dt);
}

function Row({ it, functional, onOpen }: { it: Item; functional: string; onOpen: (r: string) => void }) {
  const typeLabel = TYPE_LABELS[it.doc_type] ?? it.doc_type;
  const numLabel = it.number || '(unnumbered)';
  const title = `${typeLabel} ${numLabel}${it.party_name ? ` — ${it.party_name}` : ''}`;
  return (
    <button className="w-full flex items-center gap-2 text-left rounded-md px-2 py-0.5 transition hover:bg-row-highlight" onClick={() => onOpen(it.route)} title={title}>
      <span className="shrink-0 text-secondary" title={it.status_label}>{favoriteIcon({ kind: 'record', entity_type: it.doc_type }, 16)}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5 min-w-0">
          <span className="shrink-0 text-[0.625rem] uppercase tracking-wide text-muted">{typeLabel}</span>
          <span className={`text-[0.8125rem] truncate ${it.number ? 'text-primary-text font-semibold' : 'text-muted italic'}`}>{numLabel}</span>
        </div>
        <div className="text-[0.6875rem] truncate leading-tight">
          <span className="font-medium" style={{ color: it.status_color || '#64748b' }}>{it.status_label}</span>
          {it.party_name && <span className="text-muted"> · {it.party_name}</span>}
          {it.date && <span className="text-muted"> · {fmtDate(it.date)}</span>}
        </div>
      </div>
      {it.amount != null && (
        <div className="shrink-0 text-right text-[0.8125rem] font-medium tabular-nums text-primary-text leading-tight">{moneyFmt(it.currency || functional).format(it.amount)}</div>
      )}
    </button>
  );
}

// Section visibility from config; both sections default ON.
const sectionsOf = (config: Record<string, unknown>) => ({
  assigned: config?.show_assigned !== false,
  approvals: config?.show_approvals !== false,
});

function MyWorkRender({ config }: WidgetRenderProps) {
  const nav = useHomeNav();
  const functional = useAuthStore((s) => s.company?.functional_currency_code ?? 'USD');
  const show = sectionsOf(config);
  const q = useQuery({ queryKey: ['my-work'], queryFn: () => api.get<MyWork>('/my-work') });
  const data = q.data ?? { assigned: [], approvals: [] };
  const assigned = show.assigned ? data.assigned : [];
  const approvals = show.approvals ? data.approvals : [];
  const empty = assigned.length === 0 && approvals.length === 0;
  if (empty) return <div className="text-sm text-muted">No open work assigned to you.</div>;
  return (
    <div className="flex flex-col gap-3">
      {/* Assigned rows sit directly under the widget title ("Assigned to me") —
          no redundant section header. Approvals keep their own labelled group. */}
      {assigned.length > 0 && (
        <div className="flex flex-col">{assigned.map((it) => <Row key={`m-${it.id}`} it={it} functional={functional} onOpen={nav} />)}</div>
      )}
      {approvals.length > 0 && (
        <div>
          <div className="mb-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-amber-600">Needs approval</div>
          <div className="flex flex-col">{approvals.map((it) => <Row key={`a-${it.id}`} it={it} functional={functional} onOpen={nav} />)}</div>
        </div>
      )}
    </div>
  );
}

function MyWorkConfig({ config, onChange }: WidgetConfigProps) {
  const show = sectionsOf(config);
  const toggle = (key: 'show_assigned' | 'show_approvals', on: boolean) => onChange({ ...(config || {}), [key]: on });
  return (
    <div className="flex flex-col gap-1.5 text-[0.8125rem]">
      <Checkbox
        checked={show.assigned}
        onChange={(e) => toggle('show_assigned', e.target.checked)}
        label="Show documents assigned to me"
      />
      <Checkbox
        checked={show.approvals}
        onChange={(e) => toggle('show_approvals', e.target.checked)}
        label="Show documents waiting for my approval"
      />
    </div>
  );
}

registerWidget({ type: 'my_work', label: 'Assigned to me', defaultSize: 'M', icon: <IconCheck size={13} />, accent: '#1f8ba3', ConfigForm: MyWorkConfig, Render: MyWorkRender });
